import { useState, useCallback } from 'react'
import {
  clusterScenesWithAudio,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage audio clustering with scene structure processing
 * Handles clustering initiation and status polling
 */
export const useAudioClustering = ({
  formData,
  setFormData,
  formatSceneObj
} = {}) => {
  const [isClusteringAudio, setIsClusteringAudio] = useState(false)
  const [audioClusteringProgress, setAudioClusteringProgress] = useState(0)
  const [audioClusteringStatus, setAudioClusteringStatus] = useState('')

  /**
   * Format shot list for clustering API
   */
  const formatShotListForClustering = useCallback(shotList => {
    const sceneStructure = {}

    shotList.forEach((scene, index) => {
      const sceneKey = `scene ${index + 1}`
      const shotsObj = {}

      scene.shots.forEach((shot, sIndex) => {
        const shotKey = `shot ${sIndex + 1}`
        shotsObj[shotKey] = {
          ...shot,
          title: shot.title || '',
          shot_direction: shot.shot_direction || '',
          voiceover_text: shot.voiceover_text || '',
          initial_image_prompt: shot.initial_image_prompt || '',
          video_prompt: shot.video_prompt || '',
          scenery: shot.scenery || '',
          location: shot.location || '',
          lighting: shot.lighting || '',
          dialogue: shot.dialogue || {},
          video_duration: null
        }
      })

      sceneStructure[sceneKey] = {
        ...scene,
        scene_location: scene.scene_location || '',
        aggregated_voiceover: scene.aggregated_voiceover || '',
        audio_link: null,
        audio_duration: null,
        audio_duration_estimated: true,
        estimated_audio_duration: scene.estimated_audio_duration || null,
        shots: shotsObj
      }
    })

    return sceneStructure
  }, [])

  /*
   * Poll audio clustering task status
   */
  const pollAudioClustering = useCallback(
    async taskId => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          const isSuccess =
            response.data &&
            (response.data.status === 'success' ||
              (response.data.progress === 100 && response.data.output))

          if (isSuccess && response.data && response.data.output) {
            try {
              const sceneData = response.data.output

              setFormData(prev => {
                const currentShotList = [...(prev.shotList || [])]

                // Merge response data into existing shot list
                const updatedShotList = currentShotList.map((scene, index) => {
                  // Try to find matching scene in response
                  // The response keys might be "scene 1", "scene 2" etc. or array format
                  let responseScene = null

                  if (Array.isArray(sceneData)) {
                    responseScene = sceneData[index]
                  } else {
                    const sceneKey = `scene ${index + 1}`
                    responseScene =
                      sceneData[sceneKey] || Object.values(sceneData)[index]
                  }

                  if (!responseScene) return scene

                  // Create updated scene object
                  const updatedScene = {
                    ...scene,
                    audio_link: responseScene.audio_link || scene.audio_link,
                    audio_duration:
                      responseScene.audio_duration || scene.audio_duration,
                    scene_location:
                      responseScene.scene_location || scene.scene_location,
                    aggregated_voiceover:
                      responseScene.aggregated_voiceover ||
                      scene.aggregated_voiceover,
                    audio_duration_estimated: false // We now have real duration
                  }

                  // Update shots with video_duration from response
                  if (responseScene.shots) {
                    updatedScene.shots = scene.shots.map((shot, sIndex) => {
                      let responseShot = null

                      if (Array.isArray(responseScene.shots)) {
                        responseShot = responseScene.shots[sIndex]
                      } else {
                        const shotKey = `shot ${sIndex + 1}`
                        responseShot =
                          responseScene.shots[shotKey] ||
                          Object.values(responseScene.shots)[sIndex]
                      }

                      if (!responseShot) return shot

                      // Only take video_duration from response, preserve everything else
                      return {
                        ...shot,
                        video_duration:
                          responseShot.video_duration || shot.video_duration
                      }
                    })
                  }

                  return updatedScene
                })

                return {
                  ...prev,
                  shotList: updatedShotList,
                  audio: {
                    ...prev.audio,
                    status: 'completed'
                  }
                }
              })

              setIsClusteringAudio(false)
              break
            } catch (err) {
              console.error('Error processing audio clustering output:', err)
              setIsClusteringAudio(false)
              setAudioClusteringStatus('failed')
              break
            }
          } else if (response.data && response.data.status === 'failed') {
            console.error('Audio clustering task failed:', response.data)
            setFormData(prev => ({
              ...prev,
              audio: {
                ...prev.audio,
                status: 'failed'
              }
            }))
            setIsClusteringAudio(false)
            setAudioClusteringStatus('failed')
            break
          } else {
            setAudioClusteringProgress(
              response.data && response.data.progress
                ? response.data.progress
                : 0
            )
            setAudioClusteringStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData]
  )

  /**
   * Initiate audio clustering for scenes
   */
  const initiateAudioClustering = useCallback(
    async (sceneStructure, originalData, options = {}) => {
      setIsClusteringAudio(true)
      try {
        const payload = {
          scene_structure: sceneStructure
        }

        // Include voice_id if selected
        const voiceId = options.voiceId || formData?.selectedVoice
        if (voiceId) {
          payload.voice_id = voiceId
        }

        const response = await clusterScenesWithAudio(payload)

        const taskId = response && response.data && response.data.task_id
        if (taskId) {
          // Save task ID and set status to pending
          setFormData(prev => ({
            ...prev,
            audio: {
              id: taskId,
              status: 'pending'
            }
          }))
          await pollAudioClustering(taskId)
        } else if (response.data && response.data.output) {
          const sceneData = response.data.output

          setFormData(prev => {
            const currentShotList = [...(prev.shotList || [])]

            // Merge response data into existing shot list
            const updatedShotList = currentShotList.map((scene, index) => {
              // Try to find matching scene in response
              // The response keys might be "scene 1", "scene 2" etc. or array format
              let responseScene = null

              if (Array.isArray(sceneData)) {
                responseScene = sceneData[index]
              } else {
                const sceneKey = `scene ${index + 1}`
                responseScene =
                  sceneData[sceneKey] || Object.values(sceneData)[index]
              }

              if (!responseScene) return scene

              // Create updated scene object
              const updatedScene = {
                ...scene,
                audio_link: responseScene.audio_link || scene.audio_link,
                audio_duration:
                  responseScene.audio_duration || scene.audio_duration,
                scene_location:
                  responseScene.scene_location || scene.scene_location,
                aggregated_voiceover:
                  responseScene.aggregated_voiceover ||
                  scene.aggregated_voiceover,
                audio_duration_estimated: false // We now have real duration
              }

              // Update shots with video_duration from response
              if (responseScene.shots) {
                updatedScene.shots = scene.shots.map((shot, sIndex) => {
                  let responseShot = null

                  if (Array.isArray(responseScene.shots)) {
                    responseShot = responseScene.shots[sIndex]
                  } else {
                    const shotKey = `shot ${sIndex + 1}`
                    responseShot =
                      responseScene.shots[shotKey] ||
                      Object.values(responseScene.shots)[sIndex]
                  }

                  if (!responseShot) return shot

                  // Only take video_duration from response, preserve everything else
                  return {
                    ...shot,
                    video_duration:
                      responseShot.video_duration || shot.video_duration
                  }
                })
              }

              return updatedScene
            })

            return {
              ...prev,
              shotList: updatedShotList
            }
          })
          setIsClusteringAudio(false)
        } else {
          setIsClusteringAudio(false)
        }
      } catch (error) {
        console.error('Audio clustering failed:', error)
        setIsClusteringAudio(false)
      }
    },
    [formData?.selectedVoice, setFormData, pollAudioClustering]
  )

  /**
   * Resume polling for an existing audio clustering task (used after page refresh)
   */
  const resumeAudioPolling = useCallback(
    async taskId => {
      if (!taskId || isClusteringAudio) return

      setIsClusteringAudio(true)
      setAudioClusteringStatus('Resuming audio generation...')
      await pollAudioClustering(taskId)
    },
    [isClusteringAudio, pollAudioClustering]
  )

  /**
   * Reset audio clustering state
   */
  const resetAudioClustering = useCallback(() => {
    setIsClusteringAudio(false)
    setAudioClusteringProgress(0)
    setAudioClusteringStatus('')
  }, [])

  return {
    // State
    isClusteringAudio,
    audioClusteringProgress,
    audioClusteringStatus,

    // Actions
    initiateAudioClustering,
    formatShotListForClustering,
    resumeAudioPolling,
    resetAudioClustering
  }
}

export default useAudioClustering
